/**
 * Captura rápida desde Hoy.
 *
 * Convierte una situación real —una llamada de trabajo, algo que no se
 * entendió— en material de entrenamiento, en tres toques.
 *
 * PRIORIDAD ABSOLUTA: velocidad. Elegir el tipo, escribir una frase, guardar.
 * El resto se edita después en Banco si hace falta. Un formulario de seis
 * campos después de una llamada no se usa nunca.
 *
 * No grabar llamadas ni información sensible: acá se anota el problema
 * lingüístico, después.
 */

import { useState } from 'react';

import { AREAS, NOMBRE_AREA, type AreaOpcional } from '../../domain/modelo/areas';
import type { FuenteElemento, Kind } from '../../domain/modelo/banco';
import { bancoRepo } from '../../persistencia/bancoRepo';
import estilos from './CapturaRapida.module.css';

/**
 * Los cinco arranques de la especificación.
 *
 * Cada uno ya trae el `kind` y la `fuente` que le corresponden, para no tener
 * que elegirlos: eso es la mitad de la velocidad.
 */
const ARRANQUES: {
  clave: string;
  etiqueta: string;
  ayuda: string;
  kind: Kind;
  fuente: FuenteElemento;
  areaSugerida: AreaOpcional;
}[] = [
  {
    clave: 'quise_decir',
    etiqueta: 'Quise decir…',
    ayuda: '¿Qué querías decir y no te salió?',
    kind: 'error',
    fuente: 'conversacion',
    areaSugerida: 'recuperacion_lexica',
  },
  {
    clave: 'no_entendi',
    etiqueta: 'No entendí…',
    ayuda: '¿Qué no entendiste?',
    kind: 'error',
    fuente: 'listening',
    areaSugerida: 'listening',
  },
  {
    clave: 'segui_diciendo',
    etiqueta: 'Seguí diciendo…',
    ayuda: '¿Qué repetiste una y otra vez?',
    kind: 'error',
    fuente: 'conversacion',
    areaSugerida: 'naturalidad',
  },
  {
    clave: 'escuche',
    etiqueta: 'Escuché…',
    ayuda: '¿Qué le escuchaste decir a alguien que te sirve?',
    kind: 'expresion',
    fuente: 'conversacion',
    areaSugerida: 'naturalidad',
  },
  {
    clave: 'no_pude_reformular',
    etiqueta: 'No pude reformular…',
    ayuda: '¿Qué no supiste decir de otra manera?',
    kind: 'error',
    fuente: 'conversacion',
    areaSugerida: 'habla_espontanea',
  },
  {
    clave: 'trabajo',
    etiqueta: 'En el trabajo…',
    ayuda: '¿Qué situación del trabajo te costó?',
    kind: 'situacion_trabajo',
    fuente: 'trabajo',
    areaSugerida: null,
  },
];

export function CapturaRapida() {
  const [abierto, setAbierto] = useState(false);
  const [arranque, setArranque] = useState<(typeof ARRANQUES)[number] | null>(null);
  const [texto, setTexto] = useState('');
  const [area, setArea] = useState<AreaOpcional>(null);
  const [guardado, setGuardado] = useState(false);

  function elegir(a: (typeof ARRANQUES)[number]) {
    setArranque(a);
    setArea(a.areaSugerida);
    setTexto('');
    setGuardado(false);
  }

  function cerrar() {
    setAbierto(false);
    setArranque(null);
    setTexto('');
    setGuardado(false);
  }

  async function guardar() {
    if (!arranque || !texto.trim()) return;
    await bancoRepo.crear({
      kind: arranque.kind,
      area,
      contextoOriginal: `${arranque.etiqueta} ${texto.trim()}`,
      correccionTarget: '',
      fuente: arranque.fuente,
    });
    setGuardado(true);
    setTexto('');
    setArranque(null);
  }

  if (!abierto) {
    return (
      <button type="button" className={estilos.abrir} onClick={() => setAbierto(true)}>
        + Anotar algo que pasó
      </button>
    );
  }

  return (
    <div className={estilos.panel}>
      <div className={estilos.cabecera}>
        <h2 className={estilos.titulo}>Anotar algo que pasó</h2>
        <button type="button" className={estilos.cerrar} onClick={cerrar}>
          Cerrar
        </button>
      </div>

      {guardado && !arranque ? (
        <p className={estilos.exito} role="status">
          ✓ Guardado en el Learning Bank. Podés completarlo después en Banco.
        </p>
      ) : null}

      {!arranque ? (
        <>
          <div className={estilos.arranques}>
            {ARRANQUES.map((a) => (
              <button
                key={a.clave}
                type="button"
                className={estilos.arranque}
                onClick={() => elegir(a)}
              >
                {a.etiqueta}
              </button>
            ))}
          </div>
          <p className={estilos.nota}>
            No anotes información sensible del trabajo: acá va el problema con el
            idioma, no el contenido de la llamada.
          </p>
        </>
      ) : (
        <>
          <label className={estilos.campo}>
            <span className={estilos.etiqueta}>{arranque.ayuda}</span>
            <textarea
              className={estilos.texto}
              rows={2}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder={arranque.etiqueta}
              autoFocus
            />
          </label>

          <label className={estilos.campo}>
            <span className={estilos.etiqueta}>Área (opcional)</span>
            <select
              className={estilos.select}
              value={area ?? ''}
              onChange={(e) => setArea(e.target.value === '' ? null : (e.target.value as never))}
            >
              <option value="">Sin clasificar</option>
              {AREAS.map((a) => (
                <option key={a} value={a}>
                  {NOMBRE_AREA[a]}
                </option>
              ))}
            </select>
          </label>

          <div className={estilos.acciones}>
            <button
              type="button"
              className={estilos.secundario}
              onClick={() => setArranque(null)}
            >
              Atrás
            </button>
            <button
              type="button"
              className={estilos.principal}
              onClick={() => void guardar()}
              disabled={!texto.trim()}
            >
              Guardar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
